import { Suspense } from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { useEffect } from 'react';
import AdminRoute from './components/AdminRoute';
import HRRoute from './components/HRRoute';
import SelfServiceRoute from './components/SelfServiceRoute';
import RaterRoute from './components/RaterRoute';
import KnowledgeManagerRoute from './components/KnowledgeManagerRoute';
import AgencyRedirectRoute from './components/AgencyRedirectRoute';
import AgencyStatementsRoute from './components/AgencyStatementsRoute';
import MicrosoftCallback from './components/MicrosoftCallback';
import { pageTitleFor } from './pageTitles';
import { lazyRoute } from './services/staleBuildReload';

// Lazy-loaded page components
const Home = lazyRoute(() => import('./pages/Home'));
const KeyContacts = lazyRoute(() => import('./pages/KeyContacts'));
const CarrierSearch = lazyRoute(() => import('./pages/CarrierSearch'));
const MakePayment = lazyRoute(() => import('./pages/MakePayment'));
const CarrierPaymentPortals = lazyRoute(() => import('./pages/CarrierPaymentPortals'));
const TechnologyPage = lazyRoute(() => import('./pages/TechnologyPage'));
const AgentSupport = lazyRoute(() => import('./pages/AgentSupport'));
const AgentDirectory = lazyRoute(() => import('./pages/AgentDirectory'));
const AgencyPage = lazyRoute(() => import('./pages/AgencyPage'));
const RequestACarrier = lazyRoute(() => import('./pages/RequestACarrier'));
const SearchResults = lazyRoute(() => import('./pages/SearchResults'));
const MyAgency = lazyRoute(() => import('./pages/MyAgency'));
const AgencyStatements = lazyRoute(() => import('./pages/agencyStatements/AgencyStatements'));
const StatementDocument = lazyRoute(() => import('./pages/agencyStatements/StatementDocument'));
const AgentLibrary = lazyRoute(() => import('./pages/AgentLibrary'));
const AgencyVAResources = lazyRoute(() => import('./pages/AgencyVAResources'));
const OrderEquipment = lazyRoute(() => import('./pages/OrderEquipment'));
const AgencyOperations = lazyRoute(() => import('./pages/AgencyOperations'));
const RecruitmentAssistance = lazyRoute(() => import('./pages/operations/RecruitmentAssistance'));
const HolidaySchedule = lazyRoute(() => import('./pages/operations/HolidaySchedule'));
const StormPreparedness = lazyRoute(() => import('./pages/operations/StormPreparedness'));
const CorporatePartnershipGuide = lazyRoute(() => import('./pages/operations/CorporatePartnershipGuide'));
const OperatingManual = lazyRoute(() => import('./pages/operations/OperatingManual'));
const Vendors = lazyRoute(() => import('./pages/operations/Vendors'));
const Accounting = lazyRoute(() => import('./pages/Accounting'));
const FeatureRequest = lazyRoute(() => import('./pages/FeatureRequest'));
const MyTickets = lazyRoute(() => import('./pages/MyTickets'));
const StatusPage = lazyRoute(() => import('./pages/StatusPage'));
const Branding = lazyRoute(() => import('./pages/Branding'));
const MarketingResources = lazyRoute(() => import('./pages/MarketingResources'));
const Canva = lazyRoute(() => import('./pages/Canva'));
const CanvaAIPrompts = lazyRoute(() => import('./pages/CanvaAIPrompts'));
const CarrierDetail = lazyRoute(() => import('./pages/CarrierDetail'));
const AboutNSC = lazyRoute(() => import('./pages/AboutNSC'));
const CalendarPage = lazyRoute(() => import('./pages/CalendarPage'));
const NewsPage = lazyRoute(() => import('./pages/NewsPage'));
const NewsEditor = lazyRoute(() => import('./pages/NewsEditor'));
const EventEditor = lazyRoute(() => import('./pages/EventEditor'));
const AgentTools = lazyRoute(() => import('./pages/AgentTools'));
const Rater = lazyRoute(() => import('./pages/rater/RaterPage'));
const CarrierAppointments = lazyRoute(() => import('./pages/CarrierAppointments'));
const Settings = lazyRoute(() => import('./pages/Settings'));
const AssistantPrompt = lazyRoute(() => import('./pages/admin/AssistantPrompt'));
const AgencyRedirectList = lazyRoute(() => import('./pages/admin/agencyRedirects/AgencyRedirectList'));
const AgencyRedirectEdit = lazyRoute(() => import('./pages/admin/agencyRedirects/AgencyRedirectEdit'));
const FetchUnderwriting = lazyRoute(() => import('./pages/admin/FetchUnderwriting'));
const UserManagement = lazyRoute(() => import('./pages/admin/UserManagement'));
const UserDetail = lazyRoute(() => import('./pages/admin/UserDetail'));
const AdminControlCenter = lazyRoute(() => import('./pages/admin/AdminControlCenter'));
const DataViewer = lazyRoute(() => import('./pages/admin/DataViewer'));
const BulkHeadshots = lazyRoute(() => import('./pages/admin/BulkHeadshots'));
const AgencyPhotos = lazyRoute(() => import('./pages/admin/AgencyPhotos'));
// Event log viewer (pulls in MUI + material-react-table, kept out of the main bundle).
const LogViewer = lazyRoute(() => import('./pages/admin/LogViewer'));
const BranchEdit = lazyRoute(() => import('./pages/admin/branches/BranchEdit'));
// Documents admin (PDF upload -> Docling markdown + Bedrock extraction; pulls in MUI).
const Documents = lazyRoute(() => import('./pages/admin/documents/Documents'));
const DocumentList = lazyRoute(() => import('./pages/admin/documents/DocumentList'));
const DocumentUpload = lazyRoute(() => import('./pages/admin/documents/DocumentUpload'));
const DocumentView = lazyRoute(() => import('./pages/admin/documents/DocumentView'));
const DocumentTypeList = lazyRoute(() => import('./pages/admin/documents/DocumentTypeList'));
const DocumentTypeEdit = lazyRoute(() => import('./pages/admin/documents/DocumentTypeEdit'));
const HR = lazyRoute(() => import('./pages/HR'));
const HRDashboard = lazyRoute(() => import('./pages/HRDashboard'));
const NewGradRoadmap = lazyRoute(() => import('./pages/NewGradRoadmap'));
const MyOnboarding = lazyRoute(() => import('./pages/MyOnboarding'));
const MortgageLienholderRequest = lazyRoute(() => import('./pages/MortgageLienholderRequest'));
const CommercialCertificateRequest = lazyRoute(() => import('./pages/CommercialCertificateRequest'));
// Applied Epic self-service: the book of business, its documents, and service requests.
const AppliedEpicHome = lazyRoute(() => import('./pages/appliedEpic/AppliedEpicHome'));
const InsuredDetail = lazyRoute(() => import('./pages/appliedEpic/InsuredDetail'));
const ActivityDashboard = lazyRoute(() => import('./pages/appliedEpic/ActivityDashboard'));
const PolicyUploadStatus = lazyRoute(() => import('./pages/appliedEpic/policyUploads/PolicyUploadStatus'));
const PolicyReview = lazyRoute(() => import('./pages/appliedEpic/policyUploads/PolicyReview'));
const CallCapture = lazyRoute(() => import('./pages/CallCapture'));
const NotificationSettings = lazyRoute(() => import('./pages/NotificationSettings'));
const Notifications = lazyRoute(() => import('./pages/Notifications'));
const Assistant = lazyRoute(() => import('./pages/Assistant'));

export function usePageTitle() {
  const location = useLocation();
  useEffect(() => {
    const title = pageTitleFor(location.pathname);
    document.title = title ? `${title} | AgencyOS` : 'We Insure AgencyOS';
  }, [location.pathname]);
}

const RouteFallback = () => (
  <div style={{
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    minHeight: '50vh', color: '#979798', fontSize: '0.9rem',
  }}>
    Loading...
  </div>
);

export const AppRoutes: React.FC = () => (
  <Suspense fallback={<RouteFallback />}>
    <Routes>
      <Route path="/" element={<Home />} />
      <Route path="/login/callback" element={<Navigate to="/" replace />} />
      <Route path="/microsoft/callback" element={<MicrosoftCallback />} />
      <Route path="/search" element={<SearchResults />} />
      <Route path="/key-contacts" element={<KeyContacts />} />
      <Route path="/about-nsc" element={<AboutNSC />} />
      <Route path="/calendar" element={<CalendarPage />} />
      <Route path="/news" element={<NewsPage />} />
      <Route path="/news/new" element={<NewsEditor />} />
      <Route path="/news/edit/:id" element={<NewsEditor />} />
      <Route path="/events/new" element={<EventEditor />} />
      <Route path="/events/edit/:id" element={<EventEditor />} />
      <Route path="/agent-directory" element={<AgentDirectory />} />
      <Route path="/agency/:slug" element={<AgencyPage />} />

      {/* Agency management */}
      <Route path="/agency-management/my-agency" element={<MyAgency />} />
      <Route path="/agency-management/statements" element={<AgencyStatementsRoute><AgencyStatements /></AgencyStatementsRoute>} />
      <Route path="/agency-management/statements/:statementId" element={<AgencyStatementsRoute><StatementDocument /></AgencyStatementsRoute>} />
      <Route path="/agency-management/agent-library" element={<AgentLibrary />} />
      <Route path="/agency-management/va-resources" element={<AgencyVAResources />} />
      <Route path="/agency-management/equipment" element={<OrderEquipment />} />
      <Route path="/agency-management/operations" element={<AgencyOperations />} />
      <Route path="/agency-management/operations/recruitment-assistance" element={<RecruitmentAssistance />} />
      <Route path="/agency-management/operations/holiday-schedule" element={<HolidaySchedule />} />
      <Route path="/agency-management/operations/storm-preparedness" element={<StormPreparedness />} />
      <Route path="/agency-management/operations/corporate-partnership-guide" element={<CorporatePartnershipGuide />} />
      <Route path="/agency-management/operations/operating-manual" element={<OperatingManual />} />
      <Route path="/agency-management/operations/vendors" element={<Vendors />} />
      <Route path="/agency-profile" element={<Navigate to="/agency-management/my-agency" replace />} />

      {/* Carriers */}
      <Route path="/carrier-search" element={<CarrierSearch />} />
      <Route path="/carrier/:carrierId" element={<CarrierDetail />} />
      <Route path="/carrier/:carrierId/appointments" element={<CarrierAppointments />} />
      <Route path="/request-a-carrier" element={<RequestACarrier />} />
      <Route path="/make-payment" element={<MakePayment />} />
      <Route path="/carrier-payment-portals" element={<CarrierPaymentPortals />} />

      {/* Technology + marketing */}
      <Route path="/technology/feature-requests" element={<FeatureRequest />} />
      <Route path="/technology/:slug" element={<TechnologyPage />} />
      <Route path="/marketing/branding" element={<Branding />} />
      <Route path="/marketing/resources" element={<MarketingResources />} />
      <Route path="/marketing/canva" element={<Canva />} />
      <Route path="/marketing/canva/ai-prompts" element={<CanvaAIPrompts />} />
      <Route path="/marketing/:slug" element={<TechnologyPage />} />

      {/* Services */}
      <Route path="/services/mortgage-lienholder-request" element={<MortgageLienholderRequest />} />
      <Route path="/services/commercial-certificate-request" element={<CommercialCertificateRequest />} />

      {/* Misc */}
      <Route path="/accounting" element={<Accounting />} />
      <Route path="/agent-support" element={<AgentSupport />} />
      <Route path="/my-tickets" element={<MyTickets />} />
      <Route path="/status" element={<StatusPage />} />
      <Route path="/new-grad-roadmap" element={<NewGradRoadmap />} />
      <Route path="/my-onboarding" element={<MyOnboarding />} />

      {/* The in-portal assistant (WIPE-57). Deliberately unguarded: the whole app is behind the login
          gate, every authenticated user may ask, and the corpus holds only what AgencyOS already shows
          them. */}
      <Route path="/assistant" element={<Assistant />} />

      {/* Agent Tools */}
      <Route path="/agent-tools" element={<AgentTools />} />
      <Route path="/agent-tools/rater" element={<RaterRoute><Rater /></RaterRoute>} />
      {/* A stored quote, reopened where it was left (WIPE-1756). */}
      <Route path="/agent-tools/rater/quotes/:quoteId" element={<RaterRoute><Rater /></RaterRoute>} />

      {/* Applied Epic self-service, granted per user and by no role, admin included. Note what a grant
          confers: nothing maps an AgencyOS user to their own book of business yet, so whoever holds it
          sees all of it. */}
      <Route path="/self-service" element={<SelfServiceRoute><AppliedEpicHome /></SelfServiceRoute>} />
      <Route path="/self-service/activities" element={<SelfServiceRoute><ActivityDashboard /></SelfServiceRoute>} />
      <Route path="/self-service/insureds/:insuredId" element={<SelfServiceRoute><InsuredDetail /></SelfServiceRoute>} />
      <Route path="/self-service/policy-uploads/:batchId" element={<SelfServiceRoute><PolicyUploadStatus /></SelfServiceRoute>} />
      <Route path="/self-service/policy-uploads/:batchId/review/:documentId" element={<SelfServiceRoute><PolicyReview /></SelfServiceRoute>} />

      {/* The signed-in user's own settings. No role guard: everyone has an account to look at, and the
          page itself decides whether it can be edited. */}
      <Route path="/settings" element={<Settings />} />

      {/* Zoom call capture (WIPE-1395). No role guard: the page's content is entirely driven by
          which capture groups the signed-in user is a source or an authorized reader of, so for
          everyone else it renders as "nothing to configure". */}
      <Route path="/settings/call-capture" element={<CallCapture />} />

      {/* The signed-in user's notification inbox. No role guard: the API only ever returns the
          caller's own notifications. */}
      <Route path="/notifications" element={<Notifications />} />

      {/* Push notification opt-in. No role guard: it only ever affects the calling device. */}
      <Route path="/settings/notifications" element={<NotificationSettings />} />

      {/* HR (role-gated) */}
      <Route path="/internal-tools/hr" element={<HRRoute><HR /></HRRoute>} />
      <Route path="/internal-tools/hr/dashboard" element={<HRRoute><HRDashboard /></HRRoute>} />

      {/* Admin (role-gated) */}
      {/* The admin area opens on User Management. */}
      <Route path="/admin" element={<Navigate to="/admin/users" replace />} />
      <Route path="/admin/users" element={<AdminRoute><UserManagement /></AdminRoute>} />
      <Route path="/admin/users/:email" element={<AdminRoute><UserDetail /></AdminRoute>} />
      {/*
        Not behind AdminRoute: the grant is its own, and an admin without it would reach a page whose
        every call 403s.
      */}
      <Route path="/admin/assistant" element={<KnowledgeManagerRoute><AssistantPrompt /></KnowledgeManagerRoute>} />
      {/* Not behind AdminRoute either: the grant opens it to people who are not admins. */}
      <Route path="/admin/agency-redirects" element={<AgencyRedirectRoute><AgencyRedirectList /></AgencyRedirectRoute>} />
      <Route path="/admin/agency-redirects/new" element={<AgencyRedirectRoute><AgencyRedirectEdit /></AgencyRedirectRoute>} />
      <Route path="/admin/agency-redirects/:redirectId" element={<AgencyRedirectRoute><AgencyRedirectEdit /></AgencyRedirectRoute>} />
      {/* Also Fetch's OAuth redirect target, so its path is the stack's FetchOAuthRedirectUri. */}
      <Route path="/admin/underwriting" element={<AdminRoute><FetchUnderwriting /></AdminRoute>} />
      <Route path="/admin/control-center" element={<AdminRoute><AdminControlCenter /></AdminRoute>} />
      <Route path="/admin/data-viewer" element={<AdminRoute><DataViewer /></AdminRoute>} />
      <Route path="/admin/headshots" element={<AdminRoute><BulkHeadshots /></AdminRoute>} />
      <Route path="/admin/agency-photos" element={<AdminRoute><AgencyPhotos /></AdminRoute>} />
      <Route path="/admin/logs" element={<AdminRoute><LogViewer /></AdminRoute>} />
      <Route path="/admin/branches/edit" element={<AdminRoute><BranchEdit /></AdminRoute>} />
      <Route path="/admin/documents" element={<AdminRoute><Documents /></AdminRoute>} />
      <Route path="/admin/documents/list" element={<AdminRoute><DocumentList /></AdminRoute>} />
      <Route path="/admin/documents/upload" element={<AdminRoute><DocumentUpload /></AdminRoute>} />
      <Route path="/admin/documents/view/:documentId" element={<AdminRoute><DocumentView /></AdminRoute>} />
      <Route path="/admin/documents/types" element={<AdminRoute><DocumentTypeList /></AdminRoute>} />
      <Route path="/admin/documents/types/new" element={<AdminRoute><DocumentTypeEdit /></AdminRoute>} />
      <Route path="/admin/documents/types/:typeId" element={<AdminRoute><DocumentTypeEdit /></AdminRoute>} />
      {/* Unknown paths (CloudFront serves index.html for them) would render a blank page; go home instead. */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  </Suspense>
);
